import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { getLinkPreview, ILinkPreviewInfo, dispatchUrlPreview } from 'api/api'
import type { IOrdinalDescriptor } from 'api/api'
import type { UrlModel } from 'types/urls'
import styles from 'components/shared/linkpreview.module.scss'
import cn from 'classnames'
import { useQuery } from '@tanstack/react-query'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import usePanelContext from 'refactor/hooks/usePanelContext'
import TwitterUrl from 'components/urls/TwitterUrl'
import { isMobile } from 'lib/device'
import OrdinalsRender from 'components/answers/FileEmbed/OrdinalsRender'
import useAnswerChannel from 'state/channels/useAnswerChannel'
import useOrdinalDescriptor from 'hooks/useOrdinalDescriptor'
import { useInView } from 'react-intersection-observer'
import { mediaBucketsEnabled, RESERVED_URL_CARD_HEIGHT } from 'lib/mediaBuckets'
import { isBlockedExternalNavigation } from 'lib/externalNavigationPolicy'
import {
    decideOrdinalPreviewDispatch,
    selectOrdinalPreviewImage,
    shouldDispatchUrlPreviewAfterPreviewReady,
    type OrdinalPreviewReadyOptions,
    type OrdinalPreviewDispatchDecision,
} from 'components/shared/ordinalPreviewDispatch'
import {
    extractOrdinalOutpointFromUrl,
    isCanonicalOrdinalContentUrl,
    isGorillaPoolContentTxidUrl,
} from 'components/shared/ordinalUrlUtils'
export { extractKnovEmbedId, extractKnovEmbedTarget } from 'components/shared/knovLinkTargets'
export type { KnovEmbedTarget } from 'components/shared/knovLinkTargets'

// The pure URL-extraction/classification helpers live in linkPreviewUrls.ts (shared with the
// static-geometry height estimators, which must mirror these exact gates without importing this
// component module). Re-export them so existing import sites keep working.
import { safeHttpHref } from 'lib/safeHttpHref'
import { isYoutubeVideoUrl, twitterRegexp } from 'components/shared/linkPreviewUrls'
export {
    getKnovLinkRegexp,
    getStreamLinkRegexp,
    getLeaderboardLinkRegexp,
    getTdpLinkRegexp,
    twitterRegexp,
    youtubeRegexp,
    isYoutubeUrl,
    isYoutubeVideoUrl,
    getYoutubeVideoId,
    getUrlModelYoutubeVideoId,
    getLeaderboardEmbedPreviewKey,
    getTdpEmbedPreviewKey,
    extractStreamEmbedConfig,
    extractStreamEmbedConfigs,
    extractLeaderboardEmbedConfig,
    extractTdpEmbedConfig,
    extractUrls,
} from 'components/shared/linkPreviewUrls'
export type {
    LeaderboardEmbedConfig,
    TdpEmbedConfig,
    StreamEmbedConfig,
} from 'components/shared/linkPreviewUrls'

export const LinkPreview = React.memo(function ({
    url,
    domain,
    inViewCallback,
    urlModel = null,
    onHeadEnter,
    onHeadLeave,
    onMouseEnterAnswer,
    onMouseLeaveAnswer,
    onPreviewClick,
    onMetadataLoaded,
    answerId,
    setPostDisabled,
    estimatedWidth,
    matchingBsvAttachment,
}: {
    url: string
    domain?: string
    inViewCallback?: (options?: { allowShrink?: boolean }) => void
    urlModel?: UrlModel | null
    onMouseEnterAnswer?: (ev: React.MouseEvent) => void
    onMouseLeaveAnswer?: (ev: React.MouseEvent) => void
    onHeadEnter?: (ev: React.MouseEvent) => void
    onHeadLeave?: (ev: React.MouseEvent) => void
    onPreviewClick?: (url: string, ev: React.MouseEvent) => boolean | void
    onMetadataLoaded?: (metadata: ILinkPreviewInfo) => void
    answerId?: string
    setPostDisabled?: (disabled: boolean) => void
    estimatedWidth?: number
    matchingBsvAttachment?: {
        bsv_tx?: Record<string, any> | null
    } | null
}) {
    const { hide } = usePanelContext()
    const linkRef = useRef(null)
    const hiddenEls = useRef(null)
    const [ordinalHover, setOrdinalHover] = useState(false)
    const previewReadyDispatchRef = useRef<Set<string>>(new Set())
    // Latch the last resolved ordinal descriptor so a transient non-'resolved' refetch (EventBus
    // invalidations re-run resolveOrdinalDescriptor and can briefly return pending/null) cannot
    // collapse the resolved OrdinalsRender back to the "Resolving…" placeholder — that unmount/remount
    // re-fires OrdinalsRender's content fetch and swings the card height on every descriptor blip.
    const lastResolvedOrdinalRef = useRef<IOrdinalDescriptor | null>(null)
    const { ref: inViewRef, inView } = useInView({
        root: null,
        rootMargin: '500px 0px 500px 0px',
        threshold: 0,
    })
    const setOrdinalPreviewNode = useCallback(
        (node: HTMLDivElement | null) => {
            linkRef.current = node
            inViewRef(node)
        },
        [inViewRef],
    )

    const ordinalOutpoint = useMemo(() => extractOrdinalOutpointFromUrl(url), [url])
    const isGorillaPoolTxid = useMemo(() => isGorillaPoolContentTxidUrl(url), [url])
    const shouldUseOrdinalRender = !!ordinalOutpoint || isGorillaPoolTxid
    const { data: ordinalDescriptor } = useOrdinalDescriptor(ordinalOutpoint, {
        assumeOrigin: !!ordinalOutpoint && isCanonicalOrdinalContentUrl(url),
        enabled: !!ordinalOutpoint && shouldUseOrdinalRender,
    })
    const resolvedOrdinalDescriptor =
        ordinalDescriptor?.status === 'resolved' && ordinalDescriptor.content_url
            ? ordinalDescriptor
            : null
    if (resolvedOrdinalDescriptor) {
        lastResolvedOrdinalRef.current = resolvedOrdinalDescriptor
    }
    const stickyOrdinalDescriptor =
        lastResolvedOrdinalRef.current?.outpoint === ordinalOutpoint
            ? lastResolvedOrdinalRef.current
            : null
    const effectiveOrdinalDescriptor = resolvedOrdinalDescriptor || stickyOrdinalDescriptor
    const ordinalPreviewUrl = selectOrdinalPreviewImage({
        descriptorPreviewUrl: effectiveOrdinalDescriptor?.preview_url,
        urlPreviewUrl: urlModel?.preview_image_url || urlModel?.preview_url,
        contentType: effectiveOrdinalDescriptor?.content_type,
    })
    const ordinalPreviewDispatchDecision: OrdinalPreviewDispatchDecision =
        decideOrdinalPreviewDispatch({
            answerId,
            descriptor: effectiveOrdinalDescriptor || ordinalDescriptor,
            previewUrl: ordinalPreviewUrl,
        })
    const handlePreviewPending = useCallback(() => {
        if (setPostDisabled) setPostDisabled(true)
    }, [setPostDisabled])
    const twitterMatch = useMemo(
        () => (typeof url === 'string' ? url.match(twitterRegexp) : null),
        [url],
    )
    const navigationBlocked = useMemo(() => isBlockedExternalNavigation(url), [url])
    // Only video URLs are suppressed here (AnswerYouTubePlayer owns them);
    // playlist/channel YouTube pages render as generic link previews.
    const isYouTubeVideoLink = useMemo(() => isYoutubeVideoUrl(url), [url])
    const twitterUrlModel = useMemo(() => {
        if (!twitterMatch) return null

        const [, , , id] = twitterMatch
        return {
            ...(urlModel || {}),
            address: urlModel?.address || url,
            service_name: 'twitter',
            service_status:
                urlModel?.service_status ||
                (urlModel?.service_json ? 'twitter' : 'twitter_pending'),
            tweet_id: urlModel?.tweet_id || id,
        }
    }, [twitterMatch, url, urlModel])

    const handleOrdinalPreviewReady = useCallback(
        async (opts?: OrdinalPreviewReadyOptions) => {
            if (setPostDisabled) setPostDisabled(false)
            inViewCallback?.({ allowShrink: true })

            const dispatchKey = `${answerId || ''}:${url}:${opts?.previewUrl || ''}`
            const dispatched = previewReadyDispatchRef.current.has(dispatchKey)
            if (
                !shouldDispatchUrlPreviewAfterPreviewReady({
                    answerId,
                    previewUrl: opts?.previewUrl,
                    source: opts?.source,
                    previewAlreadyAvailable: !!ordinalPreviewUrl,
                    dispatched,
                })
            ) {
                return
            }

            previewReadyDispatchRef.current.add(dispatchKey)
            try {
                await dispatchUrlPreview({
                    answerId,
                    address: url,
                    ensureUrl: true,
                })
            } catch (err) {
                console.warn('Failed to ensure Url association after preview upload:', err)
            }
        },
        [answerId, inViewCallback, ordinalPreviewUrl, setPostDisabled, url],
    )

    const handleTwitterReady = useCallback(() => {
        inViewCallback?.({ allowShrink: true })
    }, [inViewCallback])

    useEffect(
        function onAnimationHide() {
            if (linkRef.current && hide && hiddenEls.current === null) {
                hiddenEls.current = linkRef.current.querySelectorAll('.twitter-tweet iframe')
                hiddenEls.current?.forEach(element => {
                    element.style.visibility = 'hidden'
                })
            } else {
                hiddenEls.current?.forEach(element => {
                    element.style.visibility = 'visible'
                })
                hiddenEls.current = null
            }
        },
        [hide],
    )

    if (navigationBlocked || isYouTubeVideoLink) {
        return null
    }

    if (twitterUrlModel) {
        return (
            <ErrorBoundary label="tweetLinkPreview">
                <div ref={linkRef}>
                    <TwitterUrl
                        url={twitterUrlModel}
                        answerId={answerId}
                        onReady={handleTwitterReady}
                        estimatedWidth={estimatedWidth}
                        matchingBsvAttachment={matchingBsvAttachment}
                    />
                </div>
            </ErrorBoundary>
        )
    }

    // Special-case: render ordinals via OrdinalsRender.
    if (shouldUseOrdinalRender) {
        // Flag-gated: pin the ordinal card to the same constant as the generic link card. The
        // descriptor + preview resolve asynchronously, so without the pin the card grows from the
        // 160px resolving box to whatever the resolved content wants — a scroll jump. The content
        // region flex-fills the fixed card and clips; the host footer keeps its natural height.
        const fixedOrdinalCard = mediaBucketsEnabled()
        const shouldEnsureUrl =
            // NewAnswer passes this setter to block the Post button; saved answers don't
            !setPostDisabled &&
            // Kick off UrlProcessorWorker if there's no Url record or it lacks a preview image
            (!urlModel || !urlModel?.preview_image_url) &&
            // Guard on actual saved answers (rendered replies have ids)
            !!answerId

        const resolvedOrigin = effectiveOrdinalDescriptor?.origin_outpoint || null
        const resolvedContentUrl = effectiveOrdinalDescriptor?.content_url || null

        return (
            <ErrorBoundary label="ordfsLinkPreview">
                <div
                    ref={setOrdinalPreviewNode}
                    className={cn(
                        styles.linkPreviewDisplay,
                        fixedOrdinalCard && styles.linkPreviewFixed,
                        ordinalHover && styles.hover,
                    )}
                    style={{ padding: 0 }}
                    onMouseEnter={(ev: React.MouseEvent) => {
                        setOrdinalHover(true)
                        if (onMouseLeaveAnswer) onMouseLeaveAnswer(ev)
                    }}
                    onMouseLeave={(ev: React.MouseEvent) => {
                        setOrdinalHover(false)
                        if (onMouseEnterAnswer) onMouseEnterAnswer(ev)
                    }}
                >
                    <div
                        style={
                            fixedOrdinalCard
                                ? { flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }
                                : { display: 'contents' }
                        }
                    >
                        {resolvedOrigin && resolvedContentUrl ? (
                            <OrdinalsRender
                                outpoint={resolvedOrigin}
                                url={resolvedContentUrl}
                                bsvOrdinalId={
                                    effectiveOrdinalDescriptor?.bsv_ordinal_id || undefined
                                }
                                contentType={effectiveOrdinalDescriptor?.content_type || undefined}
                                filename={effectiveOrdinalDescriptor?.filename || undefined}
                                allowInteraction={true}
                                previewUrl={ordinalPreviewUrl}
                                autoCapture3dPreview={
                                    inView &&
                                    String(effectiveOrdinalDescriptor?.content_type || '')
                                        .toLowerCase()
                                        .includes('model/gltf') &&
                                    !ordinalPreviewUrl
                                }
                                answerId={answerId}
                                setPostDisabled={setPostDisabled}
                                ensureUrl={shouldEnsureUrl}
                                forceLive={!!setPostDisabled}
                                onPreviewPending={handlePreviewPending}
                                onPreviewReady={handleOrdinalPreviewReady}
                                fromLinkPreview={true}
                                fillVideoContainer={fixedOrdinalCard}
                                previewDispatchDecision={ordinalPreviewDispatchDecision}
                            />
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    height: fixedOrdinalCard ? '100%' : 160,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    color: 'rgba(0,0,0,0.55)',
                                }}
                            >
                                Resolving ordinal…
                            </div>
                        )}
                    </div>

                    <div className={cn(styles.ordfsUrlhostContainer, ordinalHover && styles.hover)}>
                        <a
                            href={safeHttpHref(url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(styles.ordfsUrlhost)}
                            onClick={e => e.stopPropagation()}
                            title={url}
                        >
                            {extractHostFromUrl(url)}
                        </a>
                    </div>
                </div>
            </ErrorBoundary>
        )
    }

    return (
        <ErrorBoundary label="linkPreview">
            <OpenGraphLinkPreview
                url={url}
                domain={domain}
                urlModel={urlModel}
                answerId={answerId}
                inViewCallback={inViewCallback}
                onMouseEnterAnswer={onMouseEnterAnswer}
                onMouseLeaveAnswer={onMouseLeaveAnswer}
                onPreviewClick={onPreviewClick}
                onMetadataLoaded={onMetadataLoaded}
            />
        </ErrorBoundary>
    )
})

function isValidUrl(url) {
    try {
        new URL(url).hostname
        return true
    } catch {}
    return false
}

function extractHostFromUrl(url) {
    try {
        const hostname = new URL(url).hostname
        return hostname.replace(/^(www\.)?/, '')
    } catch (e) {
        return url
    }
}

interface ILinkPreviewDisplayProps extends ILinkPreviewInfo {
    url: string
    domain?: string
    urlModel?: UrlModel | null
    inViewCallback?: (options?: { allowShrink?: boolean }) => void
    onMouseEnterAnswer?: (ev: React.MouseEvent) => void
    onMouseLeaveAnswer?: (ev: React.MouseEvent) => void
    onPreviewClick?: (url: string, ev: React.MouseEvent) => boolean | void
    onMetadataLoaded?: (metadata: ILinkPreviewInfo) => void
    answerId?: string
}

function OpenGraphLinkPreview({
    url,
    domain,
    urlModel = null,
    inViewCallback,
    onMouseEnterAnswer,
    onMouseLeaveAnswer,
    onPreviewClick,
    onMetadataLoaded,
    answerId,
}: ILinkPreviewDisplayProps) {
    if (!url || !isValidUrl(url)) return <></>

    const derivedDomain = domain || extractHostFromUrl(url)

    const [hover, setHover] = useState(false)
    const [failedPreviewImage, setFailedPreviewImage] = useState<string | null>(null)

    const onMouseEnter = ev => {
        if (isMobile) return false
        if (onMouseLeaveAnswer) {
            onMouseLeaveAnswer(ev)
        }
        setHover(true)
    }

    const onMouseLeave = ev => {
        if (isMobile) return false
        if (onMouseEnterAnswer) {
            onMouseEnterAnswer(ev)
        }
        setHover(false)
    }

    const linkPreviewQuery = useQuery({
        queryKey: [gon.api.linkpreview, url],
        queryFn: () => getLinkPreview(url, answerId),
        staleTime: 6 * 60 * 60 * 1000,
        gcTime: 6 * 60 * 60 * 1000,
    })
    const loading = linkPreviewQuery.isLoading || linkPreviewQuery?.data?.status === 'pending'
    const { title, description, image } = linkPreviewQuery?.data || {}
    const persistedImage = [
        urlModel?.preview_image_url,
        urlModel?.preview_url,
        urlModel?.image_urls?.[0],
    ].find(candidate => candidate && isValidUrl(candidate))
    const previewImage = image && isValidUrl(image) ? image : persistedImage
    const renderedPreviewImage =
        previewImage && previewImage !== failedPreviewImage ? previewImage : undefined

    useEffect(() => {
        if (!linkPreviewQuery?.data || linkPreviewQuery.data.status === 'pending') return
        onMetadataLoaded?.(linkPreviewQuery.data)
    }, [linkPreviewQuery?.data, onMetadataLoaded])

    useEffect(() => {
        if (loading) return
        if (!linkPreviewQuery.isFetched && !linkPreviewQuery.isError) return
        inViewCallback?.({ allowShrink: true })
    }, [
        description,
        inViewCallback,
        linkPreviewQuery.isError,
        linkPreviewQuery.isFetched,
        loading,
        previewImage,
        title,
    ])

    const handleOtherMessage = useCallback(
        (data: { message?: string; url?: string; status?: string }) => {
            try {
                if (!data || data.message !== 'link_preview') return
                if (data.url !== url) return
                if (data.status === 'ready' || data.status === 'error') {
                    linkPreviewQuery.refetch()
                }
            } catch (e) {}
        },
        [url, linkPreviewQuery],
    )

    // Subscribe to the AnswerChannel so we can refetch once the backend worker finishes,
    // rather than polling.
    useAnswerChannel(answerId || '', handleOtherMessage)

    const handleClick = (ev: React.MouseEvent) => {
        const clickResult = onPreviewClick?.(url, ev)
        if (clickResult === false) {
            ev.preventDefault()
        }
        ev.stopPropagation()
    }

    const fixedCard = mediaBucketsEnabled()
    return (
        <div
            className={cn(
                styles.linkPreviewDisplay,
                hover && styles.hover,
                fixedCard && styles.linkPreviewFixed,
            )}
            data-testid="answer-link-preview-content"
            data-link-preview-description={description ? 'present' : 'absent'}
            style={fixedCard ? { height: RESERVED_URL_CARD_HEIGHT } : undefined}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <a
                href={safeHttpHref(url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClick}
            >
                {renderedPreviewImage ? (
                    <div
                        className={cn(styles.image, fixedCard && styles.imageFixed)}
                        data-testid="answer-link-preview-image"
                    >
                        <img
                            src={renderedPreviewImage}
                            alt=""
                            onError={() => setFailedPreviewImage(renderedPreviewImage)}
                        />
                    </div>
                ) : loading ? (
                    <LinkPreviewLoading fixed={fixedCard} />
                ) : fixedCard ? (
                    // Reserve the same image slot with a placeholder so an imageless card is the
                    // exact same height as one with an image (== the estimator constant).
                    <div
                        className={cn(styles.image, styles.imageFixed, styles.imagePlaceholder)}
                        data-testid="answer-link-preview-image"
                    >
                        <i className="fa fa-globe" />
                    </div>
                ) : (
                    <></>
                )}

                <div className={cn(styles.title)}>
                    <div className={cn(styles.favicon)}>
                        <img
                            src={`https://icon.horse/icon/${derivedDomain}?size=small`}
                            alt={derivedDomain}
                            onError={e => {
                                e.currentTarget.style.display = 'none'
                                e.currentTarget.parentElement.innerHTML =
                                    '<i class="fa fa-globe"></i>'
                            }}
                        />
                    </div>
                    <div className={cn(styles.titleText)}>{title ? title : url}</div>
                </div>
                <div
                    className={cn(styles.description)}
                    data-testid="answer-link-preview-description"
                >
                    {description}
                </div>
                <div className={cn(styles.urlhost)} data-testid="answer-link-preview-host">
                    {extractHostFromUrl(url)}
                </div>
            </a>
        </div>
    )
}

function LinkPreviewLoading({ fixed = false }: { fixed?: boolean }) {
    return (
        <div className={styles.loadingPreview} role="status" aria-label="Loading link preview">
            <div className={cn(styles.loadingImageShell, fixed && styles.imageFixed)}>
                <i className="fa fa-circle-o-notch fa-spin" />
            </div>
            <div className={styles.loadingLineLarge} />
            <div className={styles.loadingLineMedium} />
            <div className={styles.loadingLineSmall} />
        </div>
    )
}
